import { CartAddEvent, CartErrorEvent } from '@theme/events';

class VivatreeVideoCarousel extends HTMLElement {
  connectedCallback() {
    this.clearAllControlTimers?.();
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    this.abortController = new AbortController();
    this.slides = Array.from(this.querySelectorAll('.vivatree-video-carousel__slide'));
    this.viewport = this.querySelector('.vivatree-video-carousel__viewport');
    this.status = this.querySelector('.vivatree-video-carousel__status');
    this.activeIndex = Math.max(
      0,
      this.slides.findIndex((slide) => slide.classList.contains('swiper-slide-active'))
    );
    this.pointerId = null;
    this.dragged = false;
    this.suppressClick = false;
    this.controlTimers = new Map();

    if (!this.viewport || this.slides.length === 0) return;

    const { signal } = this.abortController;
    this.addEventListener('click', this.handleClick, { signal });
    this.addEventListener('play', this.handlePlay, { capture: true, signal });
    this.addEventListener('pause', this.handlePause, { capture: true, signal });
    this.addEventListener('submit', this.handleSubmit, { signal });
    this.viewport.addEventListener('keydown', this.handleKeydown, { signal });
    this.viewport.addEventListener('pointerdown', this.handlePointerDown, { signal });
    this.viewport.addEventListener('pointermove', this.handlePointerMove, { signal });
    this.viewport.addEventListener('pointerup', this.handlePointerEnd, { signal });
    this.viewport.addEventListener('pointercancel', this.handlePointerEnd, { signal });
    document.addEventListener('shopify:block:select', this.handleBlockSelect, { signal });
    this.querySelectorAll('.vivatree-video-carousel__external-video').forEach((iframe) => {
      iframe.addEventListener('load', this.handleExternalVideoLoad, { signal });
    });

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.handleResize);
      this.resizeObserver.observe(this.viewport);
    } else {
      window.addEventListener('resize', this.handleResize, { signal });
    }

    this.update(false);
    requestAnimationFrame(() => this.setAttribute('data-ready', ''));
  }

  disconnectedCallback() {
    this.pauseAll();
    this.clearAllControlTimers();
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.removeAttribute('data-ready');
    this.removeAttribute('data-dragging');
  }

  get isLooping() {
    return this.dataset.loop === 'true' && this.slides.length > 1;
  }

  get shouldAutoplay() {
    return this.dataset.autoplay === 'true';
  }

  handleResize = () => {
    this.updateLayout();
  };

  handleClick = (event) => {
    if (this.suppressClick) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const arrow = event.target.closest('[data-direction]');
    if (arrow && this.contains(arrow)) {
      this.move(arrow.dataset.direction === 'next' ? 1 : -1);
      return;
    }

    const slide = event.target.closest('.vivatree-video-carousel__slide');
    if (!slide || !this.contains(slide)) return;
    if (slide.classList.contains('swiper-slide-active')) {
      const media = event.target.closest('.vivatree-video-carousel__media');
      if (media && (media.querySelector('video') || media.querySelector('.vivatree-video-carousel__external-video'))) {
        event.preventDefault();
        if (event.target.closest('[data-video-toggle]')) {
          this.toggleMedia(media);
        } else {
          this.revealMediaControls(media);
        }
      }
      return;
    }
    const index = Number(slide.dataset.index);
    if (slide.dataset.position === 'previous' || slide.dataset.position === 'next') this.goTo(index);
  };

  handleKeydown = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    this.move(event.key === 'ArrowRight' ? 1 : -1);
  };

  handlePlay = (event) => {
    const video = event.target;
    if (!(video instanceof HTMLVideoElement)) return;
    const slide = video.closest('.vivatree-video-carousel__slide');
    if (!slide?.classList.contains('swiper-slide-active')) {
      video.pause();
      return;
    }
    this.pauseAll(video);
    this.setMediaPlaying(video.closest('.vivatree-video-carousel__media'), true);
  };

  handlePause = (event) => {
    const video = event.target;
    if (!(video instanceof HTMLVideoElement)) return;
    this.setMediaPlaying(video.closest('.vivatree-video-carousel__media'), false);
  };

  handleExternalVideoLoad = (event) => {
    const iframe = event.target;
    if (!(iframe instanceof HTMLIFrameElement)) return;
    const slide = iframe.closest('.vivatree-video-carousel__slide');
    if (slide?.classList.contains('swiper-slide-active') && this.shouldAutoplay) {
      this.playExternalVideo(iframe, true);
    } else {
      this.pauseExternalVideo(iframe);
    }
  };

  handleSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('.vivatree-video-carousel__product-form')) return;
    event.preventDefault();

    const button = form.querySelector('.vivatree-video-carousel__add-button');
    const variantId = form.dataset.variantId;
    if (!button || button.disabled || !variantId) return;

    button.disabled = true;
    button.setAttribute('data-loading', '');

    try {
      const formData = new FormData(form);
      const sectionIds = [
        ...new Set(
          Array.from(document.querySelectorAll('cart-items-component'))
            .map((item) => item.dataset.sectionId)
            .filter(Boolean)
        ),
      ];
      if (sectionIds.length > 0) formData.set('sections', sectionIds.join(','));
      formData.set('sections_url', window.location.pathname);

      const cartAddUrl =
        (typeof Theme !== 'undefined' && Theme.routes?.cart_add_url) || '/cart/add.js';
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        body: formData,
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || this.dataset.errorMessage);

      const cartUrl =
        typeof Theme !== 'undefined' && Theme.routes?.cart_url
          ? `${Theme.routes.cart_url}.js`
          : '/cart.js';
      const cartResponse = await fetch(cartUrl, { headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) throw new Error(this.dataset.errorMessage);
      const cart = await cartResponse.json();
      document.dispatchEvent(
        new CartAddEvent(cart, `video-carousel-${variantId}`, {
          source: 'video-carousel',
          itemCount: cart.item_count,
          variantId,
          sections: data.sections,
        })
      );
      if (this.status) this.status.textContent = this.dataset.addedMessage || '';
    } catch (error) {
      console.error(error);
      const message = error instanceof Error ? error.message : this.dataset.errorMessage || '';
      this.dispatchEvent(new CartErrorEvent(`video-carousel-${variantId}`, message, message, {}));
      if (this.status) this.status.textContent = message;
    } finally {
      button.removeAttribute('data-loading');
      button.disabled = false;
    }
  };

  handlePointerDown = (event) => {
    if (event.button !== 0 || event.target.closest('[data-direction]')) return;
    this.pointerId = event.pointerId;
    this.pointerStartX = event.clientX;
    this.pointerDeltaX = 0;
    this.dragged = false;
  };

  handlePointerMove = (event) => {
    if (this.pointerId !== event.pointerId) return;
    this.pointerDeltaX = event.clientX - this.pointerStartX;
    if (!this.dragged && Math.abs(this.pointerDeltaX) < 8) return;

    if (!this.dragged) {
      this.dragged = true;
      this.pauseAll();
      this.setAttribute('data-dragging', '');
      try {
        this.viewport.setPointerCapture(event.pointerId);
      } catch {}
    }

    this.viewport.style.setProperty('--vivatree-vc-drag-x', `${this.pointerDeltaX}px`);
    event.preventDefault();
  };

  handlePointerEnd = (event) => {
    if (this.pointerId !== event.pointerId) return;
    this.pointerId = null;

    if (!this.dragged) return;

    const threshold = Math.min(72, this.viewport.clientWidth * 0.14);
    const direction = this.pointerDeltaX < 0 ? 1 : -1;
    const shouldMove = Math.abs(this.pointerDeltaX) >= threshold;
    this.viewport.style.setProperty('--vivatree-vc-drag-x', '0px');
    this.removeAttribute('data-dragging');
    this.suppressClick = true;
    requestAnimationFrame(() => {
      this.suppressClick = false;
    });

    if (shouldMove) this.move(direction);
    else this.update(false);
  };

  handleBlockSelect = (event) => {
    const slide = event.target.closest?.('.vivatree-video-carousel__slide');
    if (!slide || !this.contains(slide)) return;
    this.goTo(Number(slide.dataset.index));
  };

  move(step) {
    if (this.slides.length < 2) return;
    let nextIndex = this.activeIndex + step;

    if (this.isLooping) {
      nextIndex = (nextIndex + this.slides.length) % this.slides.length;
    } else {
      nextIndex = Math.max(0, Math.min(this.slides.length - 1, nextIndex));
    }

    this.goTo(nextIndex);
  }

  goTo(index) {
    if (!Number.isInteger(index) || index < 0 || index >= this.slides.length || index === this.activeIndex) {
      this.update(false);
      return;
    }

    this.pauseAll();
    this.activeIndex = index;
    this.update(true);
  }

  update(announce) {
    this.updateLayout();
    const visibleRadius = this.getVisibleRadius();

    this.slides.forEach((slide, index) => {
      const relative = this.getRelativePosition(index);
      let position = 'hidden';
      if (relative === 0) position = 'active';
      if (relative < 0 && Math.abs(relative) <= visibleRadius) position = 'previous';
      if (relative > 0 && Math.abs(relative) <= visibleRadius) position = 'next';

      slide.dataset.position = position;
      slide.dataset.distance = String(Math.abs(relative));
      slide.classList.toggle('swiper-slide-active', position === 'active');
      slide.setAttribute('aria-hidden', position === 'active' ? 'false' : 'true');
      slide.querySelectorAll('a, button, video, iframe').forEach((control) => {
        control.tabIndex = position === 'active' ? 0 : -1;
      });
      const media = slide.querySelector('.vivatree-video-carousel__media');
      if (position !== 'active') {
        this.setControlsVisible(media, false);
      } else if (media?.dataset.playing === 'true') {
        this.scheduleControlsHide(media);
      } else {
        this.setControlsVisible(media, true);
      }
    });

    this.updateArrows();

    if (announce && this.status) {
      const activeSlide = this.slides[this.activeIndex];
      this.status.textContent = activeSlide?.getAttribute('aria-label') || '';
    }

    if (this.shouldAutoplay) this.playActive();
  }

  updateLayout() {
    if (!this.viewport) return;
    const styles = getComputedStyle(this);
    const maxWidth = Number.parseFloat(styles.getPropertyValue('--vivatree-vc-max-card-width')) || 420;
    const sideScale = Number.parseFloat(styles.getPropertyValue('--vivatree-vc-side-scale')) || 0.8;
    const gap = Number.parseFloat(styles.getPropertyValue('--vivatree-vc-gap')) || 28;
    const overlapRatio = 0.2;
    const viewportWidth = this.viewport.clientWidth;
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const visibleRadius = this.getVisibleRadius();
    const availableWidth = Math.max(220, viewportWidth - gap * 2);
    let cardWidth;
    if (isMobile) {
      cardWidth = Math.min(maxWidth, viewportWidth * 0.78);
    } else if (visibleRadius === 3) {
      let scaleTotal = 0;
      for (let distance = 1; distance <= visibleRadius; distance += 1) {
        scaleTotal += sideScale ** distance;
      }
      const scaleFactor = 1 + scaleTotal * 2 * (1 - overlapRatio);
      cardWidth = Math.min(maxWidth, Math.max(200, viewportWidth / scaleFactor));
    } else {
      cardWidth = Math.min(maxWidth, availableWidth / (1 + sideScale * 2));
    }
    const hasSlideContent = this.dataset.hasSlideContent === 'true';
    const panelHeight = hasSlideContent ? (isMobile ? 74 : 76) : 0;
    const mediaHeight = (cardWidth * 16) / 9;
    const slideHeight = mediaHeight + panelHeight;
    const offsets = [0];
    let previousScale = 1;
    let runningOffset = 0;
    for (let distance = 1; distance <= visibleRadius; distance += 1) {
      const currentScale = sideScale ** distance;
      const overlap = visibleRadius === 3 ? cardWidth * currentScale * overlapRatio : 0;
      runningOffset += (cardWidth * (previousScale + currentScale)) / 2 - overlap + (visibleRadius === 3 ? 0 : gap);
      offsets[distance] = runningOffset;
      previousScale = currentScale;
    }

    this.viewport.style.setProperty('--vivatree-vc-card-width', `${cardWidth}px`);
    this.viewport.style.setProperty('--vivatree-vc-media-height', `${mediaHeight}px`);
    this.viewport.style.setProperty('--vivatree-vc-panel-height', `${panelHeight}px`);
    this.viewport.style.setProperty('--vivatree-vc-slide-height', `${slideHeight}px`);
    this.viewport.style.setProperty('--vivatree-vc-stage-height', `${Math.ceil(slideHeight + 16)}px`);

    this.slides.forEach((slide, index) => {
      const relative = this.getRelativePosition(index);
      const distance = Math.abs(relative);
      const isVisible = distance <= visibleRadius;
      const direction = relative === 0 ? 0 : relative > 0 ? 1 : -1;
      const x = isVisible ? direction * offsets[distance] : direction * (viewportWidth + cardWidth);
      const scale = distance === 0 ? 1 : sideScale ** distance;
      const opacity = distance === 0 ? 1 : Math.max(0.38, 0.8 - (distance - 1) * 0.2);
      const rotation = visibleRadius === 3 ? direction * distance * -2.5 : 0;
      slide.style.setProperty('--vivatree-vc-slide-x', `${x}px`);
      slide.style.setProperty('--vivatree-vc-slide-scale', `${scale}`);
      slide.style.setProperty('--vivatree-vc-slide-rotate', `${rotation}deg`);
      slide.style.setProperty('--vivatree-vc-slide-opacity', isVisible ? `${opacity}` : '0');
      slide.style.setProperty('--vivatree-vc-slide-z', isVisible ? `${(visibleRadius + 1 - distance) * 10}` : '0');
    });
  }

  getVisibleRadius() {
    if (window.matchMedia('(min-width: 1200px)').matches && this.slides.length >= 7) return 3;
    return 1;
  }

  getRelativePosition(index) {
    let relative = index - this.activeIndex;
    if (!this.isLooping) return relative;
    const length = this.slides.length;
    if (relative > length / 2) relative -= length;
    if (relative < -length / 2) relative += length;
    return relative;
  }

  updateArrows() {
    const previous = this.querySelector('[data-direction="previous"]');
    const next = this.querySelector('[data-direction="next"]');
    if (!previous || !next) return;
    previous.disabled = !this.isLooping && this.activeIndex === 0;
    next.disabled = !this.isLooping && this.activeIndex === this.slides.length - 1;
  }

  pauseAll(except) {
    this.querySelectorAll('video').forEach((video) => {
      if (video === except) return;
      if (!video.paused) video.pause();
      this.setMediaPlaying(video.closest('.vivatree-video-carousel__media'), false);
    });
    this.querySelectorAll('.vivatree-video-carousel__external-video').forEach((iframe) => {
      if (iframe !== except) this.pauseExternalVideo(iframe);
    });
  }

  playActive() {
    const activeSlide = this.slides[this.activeIndex];
    const video = activeSlide?.querySelector('video');
    if (video) {
      this.pauseAll(video);
      video.muted = true;
      const promise = video.play();
      if (promise) {
        promise.catch(() => this.setMediaPlaying(video.closest('.vivatree-video-carousel__media'), false));
      }
      return;
    }

    const iframe = activeSlide?.querySelector('.vivatree-video-carousel__external-video');
    if (iframe) {
      this.pauseAll(iframe);
      this.playExternalVideo(iframe, true);
    }
  }

  toggleMedia(media) {
    const video = media.querySelector('video');
    if (video) {
      if (video.paused) {
        this.pauseAll(video);
        video.muted = false;
        const promise = video.play();
        if (promise) promise.catch(() => this.setMediaPlaying(media, false));
      } else {
        video.pause();
      }
      return;
    }

    const iframe = media.querySelector('.vivatree-video-carousel__external-video');
    if (!iframe) return;
    if (media.dataset.playing === 'true') {
      this.pauseExternalVideo(iframe);
    } else {
      this.pauseAll(iframe);
      this.playExternalVideo(iframe, false);
    }
  }

  setMediaPlaying(media, isPlaying) {
    if (!media) return;
    media.dataset.playing = isPlaying ? 'true' : 'false';
    const toggle = media.querySelector('[data-video-toggle]');
    if (!toggle) return;
    toggle.setAttribute('aria-label', isPlaying ? toggle.dataset.pauseLabel : toggle.dataset.playLabel);
    if (isPlaying) {
      this.scheduleControlsHide(media);
    } else {
      this.clearControlTimer(media);
      const slide = media.closest('.vivatree-video-carousel__slide');
      this.setControlsVisible(media, Boolean(slide?.classList.contains('swiper-slide-active')));
    }
  }

  revealMediaControls(media) {
    this.setControlsVisible(media, true);
    if (media.dataset.playing === 'true') this.scheduleControlsHide(media);
  }

  scheduleControlsHide(media) {
    this.clearControlTimer(media);
    this.setControlsVisible(media, true);
    const timer = window.setTimeout(() => {
      if (media.dataset.playing === 'true') this.setControlsVisible(media, false);
      this.controlTimers.delete(media);
    }, 2200);
    this.controlTimers.set(media, timer);
  }

  setControlsVisible(media, visible) {
    if (!media) return;
    media.dataset.controlsVisible = visible ? 'true' : 'false';
    const toggle = media.querySelector('[data-video-toggle]');
    if (!toggle) return;
    toggle.setAttribute('aria-hidden', visible ? 'false' : 'true');
    const slide = media.closest('.vivatree-video-carousel__slide');
    toggle.tabIndex = visible && slide?.classList.contains('swiper-slide-active') ? 0 : -1;
  }

  clearControlTimer(media) {
    const timer = this.controlTimers?.get(media);
    if (timer != null) window.clearTimeout(timer);
    this.controlTimers?.delete(media);
  }

  clearAllControlTimers() {
    this.controlTimers?.forEach((timer) => window.clearTimeout(timer));
    this.controlTimers?.clear();
  }

  pauseExternalVideo(iframe) {
    if (!(iframe instanceof HTMLIFrameElement) || !iframe.contentWindow) return;
    if (this.getExternalVideoProvider(iframe) === 'youtube') {
      iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), '*');
    } else {
      iframe.contentWindow.postMessage({ method: 'pause' }, '*');
    }
    this.setMediaPlaying(iframe.closest('.vivatree-video-carousel__media'), false);
  }

  playExternalVideo(iframe, muted) {
    if (!(iframe instanceof HTMLIFrameElement) || !iframe.contentWindow) return;
    if (this.getExternalVideoProvider(iframe) === 'youtube') {
      const volumeCommand = muted ? 'mute' : 'unMute';
      iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func: volumeCommand, args: [] }), '*');
      iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'playVideo', args: [] }), '*');
    } else {
      iframe.contentWindow.postMessage({ method: 'setVolume', value: muted ? 0 : 1 }, '*');
      iframe.contentWindow.postMessage({ method: 'play' }, '*');
    }
    this.setMediaPlaying(iframe.closest('.vivatree-video-carousel__media'), true);
  }

  getExternalVideoProvider(iframe) {
    return iframe.src.includes('youtube') || iframe.src.includes('youtu.be') ? 'youtube' : 'vimeo';
  }
}

if (!customElements.get('vivatree-video-carousel')) {
  customElements.define('vivatree-video-carousel', VivatreeVideoCarousel);
}
